// src/pages/Dashboard/Admin/Payments/components/PaymentBoutiqueDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { FaSave, FaShoppingBag, FaTimes } from "react-icons/fa";

import { getAllUsers } from "../../../../../services/userServices";
import { getArticles } from "../../../../../services/articlesServices";
import { createReglement } from "../../../../../services/achatsServices";
import { sendNotification } from "../../../../../services/notificationServices";

import {
  PAYMENT_STATUS,
  PAYMENT_TYPES,
  formatEuro,
  todayISO,
  toAmount,
} from "../utils/paymentsDashboardUtils";

function DrawerField({ label, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      {children}

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 16,
    borderColor: state.isFocused ? "#818cf8" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(129, 140, 248, .45)" : "none",
    backgroundColor: "transparent",
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: 12,
    paddingRight: 8,
  }),
};

export default function PaymentBoutiqueDrawer({ isOpen, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);

  const [selectedUser, setSelectedUser] = useState("");
  const [selectedArticle, setSelectedArticle] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [dateAchat, setDateAchat] = useState(todayISO());
  const [messageAchat, setMessageAchat] = useState("");
  const [statutReglement, setStatutReglement] = useState("Non Réglé");
  const [typeReglement, setTypeReglement] = useState("En ligne");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const token = useMemo(() => localStorage.getItem("token") || "", []);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;

    async function loadData() {
      try {
        setLoading(true);

        const [usersData, articlesData] = await Promise.all([
          getAllUsers(),
          getArticles(),
        ]);

        if (!mounted) return;

        setUsers(Array.isArray(usersData) ? usersData : []);
        setArticles(Array.isArray(articlesData) ? articlesData : []);
        setDateAchat(todayISO());
      } catch (error) {
        console.error("Erreur chargement drawer boutique :", error);
        toast.error("Erreur lors du chargement des données.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onEsc = (event) => {
      if (event.key === "Escape" && !submitting) onClose?.();
    };

    window.addEventListener("keydown", onEsc);

    return () => window.removeEventListener("keydown", onEsc);
  }, [isOpen, onClose, submitting]);

  const userOptions = useMemo(() => {
    return users.map((user) => {
      const adherentsLabel = user.adherents?.length
        ? ` (${user.adherents
            .map((adherent) =>
              `${adherent?.prenom || ""} ${adherent?.nom || ""}`.trim()
            )
            .filter(Boolean)
            .join(", ")})`
        : "";

      return {
        value: user.id,
        label: `${user.nom || ""} ${user.prenom || ""}${adherentsLabel}`.trim(),
      };
    });
  }, [users]);

  const selectedArticleObj = useMemo(() => {
    return articles.find(
      (article) => String(article.id) === String(selectedArticle)
    );
  }, [articles, selectedArticle]);

  const total = useMemo(() => {
    return toAmount(selectedArticleObj?.prix) * (Number(quantity) || 0);
  }, [selectedArticleObj, quantity]);

  const resetForm = () => {
    setSelectedUser("");
    setSelectedArticle("");
    setQuantity(1);
    setDateAchat(todayISO());
    setMessageAchat("");
    setStatutReglement("Non Réglé");
    setTypeReglement("En ligne");
  };

  const closeDrawer = () => {
    if (submitting) return;

    resetForm();
    onClose?.();
  };

  const handleSendNotification = async (recipientId) => {
    const message =
      "Le responsable du club vous a affecté un règlement d'un article de la boutique à régler sur la page Commandes & Règlements.";

    await sendNotification(message, recipientId, token);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedUser) {
      toast.warn("Sélectionnez un utilisateur.");
      return;
    }

    if (!selectedArticle) {
      toast.warn("Sélectionnez un article.");
      return;
    }

    if (!statutReglement) {
      toast.warn("Sélectionnez un statut de règlement.");
      return;
    }

    if (!typeReglement) {
      toast.warn("Sélectionnez un type de règlement.");
      return;
    }

    try {
      setSubmitting(true);

      await createReglement({
        user_achat_id: selectedUser,
        article_id: selectedArticle,
        quantite: Number(quantity) || 1,
        date_achat: dateAchat,
        message_achat: messageAchat,
        statut_reglement: statutReglement,
        type_reglement: typeReglement,
      });

      await handleSendNotification(selectedUser);

      toast.success("Achat boutique ajouté avec succès.");

      await onSaved?.();

      resetForm();
      onClose?.();
    } catch (error) {
      console.error("Erreur création achat boutique :", error);
      toast.error("Erreur lors de la création de l’achat boutique.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={closeDrawer}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Paiement boutique
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaShoppingBag className="text-indigo-600 dark:text-indigo-300" />
              Ajouter un achat boutique
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Affectez manuellement un article boutique à un utilisateur :
              kimono, textile, passeport sportif, stage, événement ou autre
              produit vendu par le club.
            </p>
          </div>

          <button
            type="button"
            onClick={closeDrawer}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          onKeyDown={(event) => {
            if (event.key === "Enter" && event.target.tagName !== "TEXTAREA") {
              event.preventDefault();
            }
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <DrawerField label="Utilisateur *">
                <Select
                  classNamePrefix="rs"
                  options={userOptions}
                  value={
                    userOptions.find(
                      (option) => String(option.value) === String(selectedUser)
                    ) || null
                  }
                  onChange={(option) => setSelectedUser(option?.value || "")}
                  placeholder="Sélectionnez un utilisateur..."
                  isClearable
                  isDisabled={loading || submitting}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </DrawerField>

              <DrawerField label="Date d’achat *">
                <input
                  type="date"
                  value={dateAchat}
                  onChange={(event) => setDateAchat(event.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                />
              </DrawerField>
            </div>

            <DrawerField label="Article *">
              <select
                value={selectedArticle}
                onChange={(event) => setSelectedArticle(event.target.value)}
                disabled={loading || submitting}
                required
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              >
                <option value="">— Sélectionnez un article —</option>

                {articles.map((article) => (
                  <option key={article.id} value={article.id}>
                    {article.nom}
                  </option>
                ))}
              </select>

              {selectedArticleObj ? (
                <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Prix unitaire : {formatEuro(selectedArticleObj.prix)}
                </p>
              ) : null}
            </DrawerField>

            <div className="grid gap-4 sm:grid-cols-3">
              <DrawerField label="Quantité *">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(event) =>
                    setQuantity(Math.max(1, Number(event.target.value || 1)))
                  }
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                />
              </DrawerField>

              <DrawerField label="Statut *">
                <select
                  value={statutReglement}
                  onChange={(event) => setStatutReglement(event.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  {PAYMENT_STATUS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </DrawerField>

              <DrawerField label="Type de règlement *">
                <select
                  value={typeReglement}
                  onChange={(event) => setTypeReglement(event.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  {PAYMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </DrawerField>
            </div>

            <DrawerField
              label="Message utilisateur"
              hint="Ce message sera visible côté utilisateur dans le détail du règlement."
            >
              <textarea
                value={messageAchat}
                onChange={(event) => setMessageAchat(event.target.value)}
                rows={5}
                placeholder="Ex : taille souhaitée, précision sur le règlement, remise en main propre..."
                className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
            </DrawerField>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Résumé du paiement
                  </p>

                  <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {selectedArticleObj
                      ? `${selectedArticleObj.nom} · ${formatEuro(
                          selectedArticleObj.prix
                        )} × ${quantity}`
                      : "Aucun article sélectionné"}
                  </p>
                </div>

                <div className="rounded-2xl bg-white px-4 py-3 text-right shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                    Total
                  </p>

                  <p className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                    {formatEuro(total)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeDrawer}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={submitting || loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : "Créer l’achat"}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}